import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

const TypeOfIssuesChart = ({ data = [] }) => {
    const typeOfIssuesChartRef = useRef(null);
    const typeOfIssuesChartInstance = useRef(null);

    // Default data if none provided
    const defaultData = [
        { type: 'Records Not Updating', count: 30 },
        { type: 'Booked a machine but cancelled', count: 55 },
        { type: 'Not able to login', count: 48 },
        { type: 'Not receiving notification', count: 95 },
        { type: 'Not able to add projects', count: 45 },
    ];

    // Helper function to wrap labels into multiple lines
    const wrapLabel = (label, maxLength = 20) => {
        if (!label) return '';
        if (label.length <= maxLength) return label;

        const words = label.split(' ');
        const lines = [];
        let currentLine = '';

        words.forEach(word => {
            if ((currentLine + word).length <= maxLength) {
                currentLine += (currentLine ? ' ' : '') + word;
            } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            }
        });
        if (currentLine) lines.push(currentLine);
        return lines;
    };

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => wrapLabel(item.type || item.issue_type || item.label));
    const values = chartData.map(item => item.count || item.value || 0);
    const maxValue = Math.max(...values, 10);

    useEffect(() => {
        if (typeOfIssuesChartRef.current) {
            if (typeOfIssuesChartInstance.current) {
                typeOfIssuesChartInstance.current.destroy();
            }

            // Calculate appropriate x-axis max
            const stepSize = Math.ceil(maxValue / 4 / 10) * 10 || 10;
            const xMax = Math.ceil(maxValue * 1.2 / stepSize) * stepSize;

            const ctx = typeOfIssuesChartRef.current.getContext('2d');
            typeOfIssuesChartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'No. of Users',
                            data: values,
                            backgroundColor: '#fde9d9', // Soft peach
                            borderColor: '#f5ac78',     // Orange border
                            borderWidth: 1,
                            borderRadius: 0,           // Square corners for clean look
                            barThickness: 28,
                        },
                    ],
                },
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: '#333',
                            titleFont: { family: 'Inter, sans-serif' },
                            bodyFont: { family: 'Inter, sans-serif' },
                            callbacks: {
                                label: (context) => `Count: ${context.raw}`
                            }
                        },
                        datalabels: { display: false },
                    },
                    layout: {
                        padding: {
                            top: 10,
                            right: 20,
                            bottom: 10,
                        }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            max: xMax,
                            ticks: {
                                stepSize: stepSize,
                                color: '#666666',
                                font: { size: 12, family: 'Inter, sans-serif' },
                            },
                            grid: {
                                color: '#e0e0e0',
                                drawOnChartArea: true,
                                drawTicks: true
                            },
                            border: {
                                display: true,
                                color: '#333333',
                            }
                        },
                        y: {
                            grid: { display: false },
                            border: {
                                display: true,
                                color: '#333333',
                            },
                            ticks: {
                                color: '#333333',
                                font: { size: 11, family: 'Inter, sans-serif' },
                                padding: 15,
                            },
                        },
                    },
                },
            });
        }
        return () => {
            if (typeOfIssuesChartInstance.current) {
                typeOfIssuesChartInstance.current.destroy();
            }
        };
    }, [data, labels, values, maxValue]);

    const hasData = chartData.some(item => (item.count || item.value || 0) > 0);

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#ffffff',
                border: '1px solid #e6e6e6',
                borderRadius: '20px', // Premium rounded corners
                padding: '20px',
                gap: '10px'
            }}
        >
            {/* Vertical Y-axis Label */}
            <Typography
                sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    color: '#333333',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                    whiteSpace: 'nowrap'
                }}
            >
                No. of Users
            </Typography>

            <Box sx={{ flex: 1, height: '100%', position: 'relative' }}>
                {!hasData && data.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography sx={{ color: '#999', fontSize: '14px' }}>
                            No issue types data available
                        </Typography>
                    </Box>
                ) : (
                    <canvas ref={typeOfIssuesChartRef} style={{ width: '100%', height: '100%' }} />
                )}
            </Box>
        </Box>
    );
};

export default TypeOfIssuesChart;
